/*
 * ProcessSection — robust, premium 5-step delivery timeline.
 * Replaces the earlier scroll-pinned horizontal experiment (which clipped cards
 * off-screen and left a tall empty band). No scroll-jacking, no horizontal
 * overflow: a connected horizontal timeline on desktop and a clean vertical
 * timeline on tablet/mobile. Every step is always fully visible and readable.
 */
import { Search, GitBranch, ListChecks, Boxes, ShieldCheck } from "lucide-react";
import { SectionHeading } from "./SectionHeading";
import { ScrollReveal } from "@/components/motion/ScrollReveal";
import { Tilt } from "@/components/motion/Tilt";
import { useLang } from "@/i18n/LanguageContext";

const ICONS = [Search, GitBranch, ListChecks, Boxes, ShieldCheck];
const CODES = ["scan(workbook)", "map(deps)", "extract(rules)", "build(secure)", "validate(parity)"];
const pad = (n) => String(n).padStart(2, "0");

export const ProcessSection = () => {
  const { t } = useLang();
  const p = t.process;
  const steps = p.steps;

  return (
    <section id="proces" className="relative scroll-mt-24 py-24 lg:py-32 overflow-hidden">
      <div className="absolute inset-0 bg-grid bg-grid-fade pointer-events-none" style={{ zIndex: 0 }} />
      <div
        className="glow-orb animate-glow pointer-events-none"
        style={{ zIndex: 0, width: 540, height: 360, top: -40, left: "50%", transform: "translateX(-50%)", background: "var(--blue)", opacity: 0.14 }}
      />

      <div className="relative z-10 mx-auto max-w-7xl px-5">
        <SectionHeading eyebrow={p.eyebrow} title={p.title} intro={p.intro} testId="process-heading" />

        {/* Desktop: connected horizontal timeline (all 5 steps visible, no scroll) */}
        <div className="hidden lg:block mt-16" data-testid="process-timeline">
          <div className="relative">
            {/* animated connector rail behind the node row */}
            <div className="absolute left-[10%] right-[10%] top-7 h-px bg-gradient-to-r from-[var(--blue)] via-[var(--cyan)] to-[var(--green)] opacity-60" />
            <div className="grid grid-cols-5 gap-5">
              {steps.map((s, i) => {
                const Icon = ICONS[i];
                return (
                  <ScrollReveal key={i} delay={i * 0.08}>
                    <div className="flex flex-col items-center" data-testid={`process-step-${i + 1}`}>
                      <div className="relative z-10 grid place-items-center h-14 w-14 rounded-2xl panel text-[var(--blue-bright)] shadow-[0_0_26px_-6px_var(--glow-cyan)]">
                        <Icon size={22} />
                        <span className="absolute -top-2 -right-2 grid place-items-center h-6 w-6 rounded-full bg-[var(--bg-2)] border border-[var(--line-strong)] font-mono text-[10px] text-[var(--cyan)]">
                          {pad(i + 1)}
                        </span>
                      </div>
                      <Tilt className="mt-6 w-full">
                        <div className="glass-card depth-card sheen rounded-2xl p-5 h-full">
                          <span className="font-mono text-[11px] text-[var(--green)] uppercase tracking-widest">{CODES[i]}</span>
                          <h3 className="mt-2 font-display text-base font-semibold text-white leading-snug">{s.title}</h3>
                          <p className="mt-2 text-sm text-muted leading-relaxed">{s.desc}</p>
                        </div>
                      </Tilt>
                    </div>
                  </ScrollReveal>
                );
              })}
            </div>
          </div>
        </div>

        {/* Tablet / mobile: vertical timeline */}
        <div className="lg:hidden mt-12">
          <div className="relative pl-8">
            <div className="absolute left-[9px] top-3 bottom-3 w-px bg-gradient-to-b from-[var(--blue)] via-[var(--cyan)] to-[var(--green)] opacity-50" />
            <div className="space-y-5">
              {steps.map((s, i) => {
                const Icon = ICONS[i];
                return (
                  <ScrollReveal key={i} delay={i * 0.05}>
                    <div className="relative" data-testid={`process-step-m-${i + 1}`}>
                      <span className="absolute -left-8 top-1.5 grid place-items-center h-5 w-5 rounded-full bg-[var(--blue)] text-white font-mono text-[10px] border border-[var(--cyan)]">
                        {i + 1}
                      </span>
                      <div className="glass-card rounded-2xl p-5">
                        <div className="flex items-center gap-3">
                          <span className="grid place-items-center h-10 w-10 rounded-xl panel text-[var(--blue-bright)]">
                            <Icon size={20} />
                          </span>
                          <span className="font-mono text-[11px] text-[var(--green)] uppercase tracking-widest">{CODES[i]}</span>
                        </div>
                        <h3 className="mt-3 font-display text-base font-semibold text-white">{s.title}</h3>
                        <p className="mt-2 text-sm text-muted leading-relaxed">{s.desc}</p>
                      </div>
                    </div>
                  </ScrollReveal>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
